import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from '@kostbar/i18n'
import { useVirtualizer } from '@tanstack/react-virtual'
import {
  ChevronDown,
  ChevronRight,
  Download,
  Layers,
  List,
  Loader2,
  Search,
} from 'lucide-react'
import {
  endOfISOWeek,
  format,
  setISOWeek,
  setISOWeekYear,
  startOfISOWeek,
} from 'date-fns'
import {
  useSalesFilters,
  useSalesGroupedQuery,
  useSalesInfiniteQuery,
  useSalesQuery,
} from '../../api/use-sales-query'
import {
  formatCurrency,
  formatDatetime,
  formatGroupKey,
} from '../../lib/format'
import { exportCsv } from '../../lib/export-csv'
import { getRowClassName } from '../../lib/colors'
import { resolveDatePreset } from '../../lib/date-presets'
import { DateRangeFilter } from '../../components/date-range-filter'
import { MultiSelectFilter } from '../../components/multi-select-filter'
import {
  CHANNEL_BADGES,
  DATE_GROUP_KEYS,
  ORDER_STATUS_BADGES,
  ORDER_STATUS_OPTIONS,
  PAYMENT_METHOD_OPTIONS,
  PAYMENT_STATUS_BADGES,
  PAYMENT_STATUS_OPTIONS,
  SALES_COLUMNS,
  SALES_GROUPING_OPTIONS,
  SALES_TABS,
} from './sales-config'
import { SalesDetailDrawer } from './sales-detail-drawer'
import type { SalesRow } from '../../api/use-sales-query'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { PermissionGate } from '@/shared/components/permission-gate'
import { ACTIONS } from '@/shared/constants/actions'
import { useRestaurants } from '@/features/products/hooks/use-restaurants'

const initialRange = resolveDatePreset('today')

export function SalesReportPage() {
  const { t } = useTranslation('reports')
  const { data: restaurantsData } = useRestaurants()
  const restaurants = restaurantsData?.items ?? []

  const [activeTab, setActiveTab] = useState('all')
  const [restaurantId, setRestaurantId] = useState<Array<string>>([])
  const [sort] = useState('occurredAt')
  const [sortDir] = useState<'asc' | 'desc'>('desc')
  const [search, setSearch] = useState('')
  const [dateFrom, setDateFrom] = useState(initialRange.from)
  const [dateTo, setDateTo] = useState(initialRange.to)
  const [orderStatus, setOrderStatus] = useState<Array<string>>([])
  const [paymentStatus, setPaymentStatus] = useState<Array<string>>([])
  const [paymentMethod, setPaymentMethod] = useState<Array<string>>([])
  const [splitPayment, setSplitPayment] = useState<Array<string>>([])
  const [operatorId, setOperatorId] = useState<Array<string>>([])
  const [stationId, setStationId] = useState<Array<string>>([])

  const [groupBy, setGroupBy] = useState<string | null>(null)
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null)

  const groupedOptions = useMemo(() => {
    const byCategory: Record<string, typeof SALES_GROUPING_OPTIONS> = {}
    for (const o of SALES_GROUPING_OPTIONS) {
      const cat = o.category ?? ''
      ;(byCategory[cat] ??= []).push(o)
    }
    return Object.entries(byCategory)
  }, [])

  const [selectedSale, setSelectedSale] = useState<{
    id: string
    channel: string
  } | null>(null)

  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // Auto-select first restaurant
  if (restaurantId.length === 0 && restaurants.length > 0) {
    setRestaurantId([String(restaurants[0].id)])
  }

  const { data: filterOptions } = useSalesFilters(
    restaurantId.length === 1 ? Number(restaurantId[0]) : null,
  )

  const tabFilters = useMemo(() => {
    const tab = SALES_TABS.find((entry) => entry.key === activeTab)
    return tab?.defaultFilters ?? {}
  }, [activeTab])

  const groupSortDir = groupBy && DATE_GROUP_KEYS.has(groupBy) ? 'desc' : 'asc'

  const filters = useMemo(
    () => ({
      ...tabFilters,
      restaurantId: restaurantId.length > 0 ? restaurantId : undefined,
      dateFrom: dateFrom ? dateFrom + 'T00:00:00Z' : undefined,
      dateTo: dateTo ? dateTo + 'T23:59:59Z' : undefined,
      orderStatus: orderStatus.length > 0 ? orderStatus : undefined,
      paymentStatus: paymentStatus.length > 0 ? paymentStatus : undefined,
      paymentMethod: paymentMethod.length > 0 ? paymentMethod : undefined,
      splitPayment: splitPayment.length > 0 ? splitPayment : undefined,
      operatorId: operatorId.length > 0 ? operatorId : undefined,
      stationId: stationId.length > 0 ? stationId : undefined,
      q: search || undefined,
      sort,
      sortDir,
      groupSortDir: groupBy ? groupSortDir : undefined,
    }),
    [
      tabFilters,
      restaurantId,
      dateFrom,
      dateTo,
      orderStatus,
      paymentStatus,
      paymentMethod,
      splitPayment,
      operatorId,
      stationId,
      search,
      sort,
      sortDir,
      groupBy,
      groupSortDir,
    ],
  )

  const isGrouped = groupBy !== null
  const flatQuery = useSalesInfiniteQuery(filters, !isGrouped)
  const groupedQuery = useSalesGroupedQuery(filters, groupBy ?? '', isGrouped)

  const allRows = useMemo(
    () => flatQuery.data?.pages.flatMap((p) => p.rows) ?? [],
    [flatQuery.data],
  )
  const loadedRows = allRows.length
  const flatTotalRows = flatQuery.data?.pages[0]?.totalRows ?? 0
  const flatTotalGross = flatQuery.data?.pages[0]?.totals.totalGrossCents ?? 0

  const expandFilters = useMemo(
    () =>
      expandedGroup && groupBy
        ? buildExpandFilters(groupBy, expandedGroup, filters)
        : null,
    [expandedGroup, groupBy, filters],
  )

  const expandQuery = useSalesQuery(expandFilters ?? {}, expandFilters !== null)

  const handleTabChange = useCallback((tab: string) => {
    setActiveTab(tab)
    setExpandedGroup(null)
  }, [])

  const handleExport = useCallback(() => {
    if (!allRows.length) return
    exportCsv(
      allRows,
      SALES_COLUMNS,
      `verkauefe-${new Date().toISOString().slice(0, 10)}.csv`,
    )
  }, [allRows])

  const toggleGroup = useCallback((key: string) => {
    setExpandedGroup((prev) => (prev === key ? null : key))
  }, [])

  const formatGroupLabel = useCallback(
    (gb: string, key: string): string => {
      const dateLabel = formatGroupKey(gb, key)
      if (dateLabel) return dateLabel

      switch (gb) {
        case 'restaurantId':
          return (
            restaurants.find((r) => String(r.id) === key)?.name ??
            t('sales.groupLocationFallback', { key })
          )
        case 'splitPayment':
          return key === 'true' ? t('filters.yes') : t('filters.no')
        case 'channel':
          return CHANNEL_BADGES[key]?.label ?? key
        case 'orderStatus':
          return ORDER_STATUS_BADGES[key]?.label ?? key
        case 'paymentStatus':
          return PAYMENT_STATUS_BADGES[key]?.label ?? key
        default:
          return key === '–' ? t('sales.groupUnassigned') : key
      }
    },
    [restaurants, t],
  )

  // Virtualization for flat mode
  const virtualItemCount = loadedRows + (flatQuery.isFetchingNextPage ? 1 : 0)
  const virtualizer = useVirtualizer({
    count: virtualItemCount,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => 40,
    overscan: 10,
  })

  const virtualItems = virtualizer.getVirtualItems()
  const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0
  const paddingBottom =
    virtualItems.length > 0
      ? virtualizer.getTotalSize() - virtualItems[virtualItems.length - 1].end
      : 0

  // Infinite scroll trigger
  const lastVirtualItemIndex =
    virtualItems.length > 0 ? virtualItems[virtualItems.length - 1].index : -1

  useEffect(() => {
    if (isGrouped || !flatQuery.hasNextPage || flatQuery.isFetchingNextPage)
      return
    if (lastVirtualItemIndex < 0) return

    const threshold = Math.max(
      virtualItemCount - Math.ceil(virtualItemCount * 0.2),
      0,
    )
    if (lastVirtualItemIndex >= threshold) {
      flatQuery.fetchNextPage()
    }
  }, [
    lastVirtualItemIndex,
    isGrouped,
    flatQuery.hasNextPage,
    flatQuery.isFetchingNextPage,
    virtualItemCount,
  ])

  const isLoading = isGrouped ? groupedQuery.isLoading : flatQuery.isLoading
  const totalRows = isGrouped
    ? (groupedQuery.data?.totalRows ?? 0)
    : flatTotalRows
  const totalGross = isGrouped
    ? (groupedQuery.data?.totals.totalGrossCents ?? 0)
    : flatTotalGross

  return (
    <PermissionGate action={ACTIONS.TRANSACTION_VIEW}>
      <div className="flex h-full flex-col gap-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">{t('sales.title')}</h1>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setGroupBy(groupBy ? null : 'month')}
            >
              {groupBy ? (
                <List className="mr-1 h-4 w-4" />
              ) : (
                <Layers className="mr-1 h-4 w-4" />
              )}
              {groupBy ? t('sales.viewFlat') : t('sales.viewGrouped')}
            </Button>
            {!isGrouped && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
                disabled={!allRows.length}
              >
                <Download className="mr-1 h-4 w-4" />
                {t('sales.exportCsv')}
              </Button>
            )}
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList>
            {SALES_TABS.map((tab) => (
              <TabsTrigger key={tab.key} value={tab.key}>
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {/* Filters toolbar */}
        <div className="flex flex-wrap items-center gap-2">
          {restaurants.length > 1 && (
            <MultiSelectFilter
              label={t('filters.location')}
              options={restaurants.map((r) => ({
                value: r.id.toString(),
                label: r.name,
              }))}
              selected={restaurantId}
              onChange={setRestaurantId}
            />
          )}

          <DateRangeFilter
            dateFrom={dateFrom}
            dateTo={dateTo}
            onApply={(from, to) => {
              setDateFrom(from)
              setDateTo(to)
            }}
          />

          <MultiSelectFilter
            label={t('filters.orderStatus')}
            options={ORDER_STATUS_OPTIONS}
            selected={orderStatus}
            onChange={setOrderStatus}
          />

          <MultiSelectFilter
            label={t('filters.paymentStatus')}
            options={PAYMENT_STATUS_OPTIONS}
            selected={paymentStatus}
            onChange={setPaymentStatus}
          />

          <MultiSelectFilter
            label={t('filters.paymentMethod')}
            options={PAYMENT_METHOD_OPTIONS}
            selected={paymentMethod}
            onChange={setPaymentMethod}
          />

          <MultiSelectFilter
            label={t('filters.splitPayment')}
            options={[
              { value: 'true', label: t('filters.yes') },
              { value: 'false', label: t('filters.no') },
            ]}
            selected={splitPayment}
            onChange={setSplitPayment}
          />

          {activeTab !== 'online' && filterOptions && (
            <>
              {filterOptions.operators.length > 0 && (
                <MultiSelectFilter
                  label={t('filters.operator')}
                  options={filterOptions.operators.map((o) => ({
                    value: o,
                    label: o,
                  }))}
                  selected={operatorId}
                  onChange={setOperatorId}
                />
              )}
              {filterOptions.stations.length > 0 && (
                <MultiSelectFilter
                  label={t('filters.station')}
                  options={filterOptions.stations.map((s) => ({
                    value: s,
                    label: s,
                  }))}
                  selected={stationId}
                  onChange={setStationId}
                />
              )}
            </>
          )}

          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              className="w-[200px] pl-8"
              placeholder={t('sales.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {isGrouped && (
            <Select
              value={groupBy}
              onValueChange={(v) => {
                setGroupBy(v)
                setExpandedGroup(null)
              }}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder={t('sales.groupingPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {groupedOptions.map(([category, options]) => (
                  <SelectGroup key={category}>
                    <SelectLabel>{category}</SelectLabel>
                    {options.map((o) => (
                      <SelectItem key={o.key} value={o.key}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Data */}
        {isLoading && (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        )}

        {!isLoading && !isGrouped && flatQuery.data && (
          <div className="flex min-h-0 flex-1 flex-col rounded-md border">
            <div
              ref={scrollContainerRef}
              className="min-h-0 flex-1 overflow-auto"
            >
              <table className="w-full">
                <thead className="sticky top-0 z-10 bg-background">
                  <tr className="border-b">
                    {SALES_COLUMNS.map((col) => (
                      <th
                        key={col.key}
                        className="px-4 py-2 text-left text-sm font-medium text-muted-foreground"
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {allRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={SALES_COLUMNS.length}
                        className="py-6 text-center text-muted-foreground"
                      >
                        {t('sales.noResults')}
                      </td>
                    </tr>
                  ) : (
                    <>
                      {paddingTop > 0 && (
                        <tr>
                          <td
                            colSpan={SALES_COLUMNS.length}
                            style={{ height: paddingTop }}
                          />
                        </tr>
                      )}
                      {virtualItems.map((virtualRow) => {
                        if (virtualRow.index >= loadedRows) {
                          return (
                            <tr key="loading">
                              <td
                                colSpan={SALES_COLUMNS.length}
                                className="py-3 text-center"
                              >
                                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                                  <Loader2 className="size-4 animate-spin" />
                                  {t('sales.loadingMore')}
                                </div>
                              </td>
                            </tr>
                          )
                        }
                        const row = allRows[virtualRow.index]
                        return (
                          <tr
                            key={row.id}
                            className={`cursor-pointer border-b transition-colors hover:bg-muted/50 ${getRowClassName('status', row.orderStatus)}`}
                            onClick={() =>
                              setSelectedSale({
                                id: row.id,
                                channel: row.channel,
                              })
                            }
                          >
                            {SALES_COLUMNS.map((col) => (
                              <td
                                key={col.key}
                                className={`px-4 py-2 text-sm ${col.className ?? ''}`}
                              >
                                <CellRenderer column={col} row={row} />
                              </td>
                            ))}
                          </tr>
                        )
                      })}
                      {paddingBottom > 0 && (
                        <tr>
                          <td
                            colSpan={SALES_COLUMNS.length}
                            style={{ height: paddingBottom }}
                          />
                        </tr>
                      )}
                    </>
                  )}
                </tbody>
              </table>
            </div>

            {/* Sticky footer with totals */}
            <div className="shrink-0 border-t bg-muted/30">
              <table className="w-full">
                <tfoot>
                  <tr>
                    <td
                      colSpan={SALES_COLUMNS.length - 5}
                      className="px-4 py-2 text-sm font-semibold"
                    >
                      {t('sales.totalRows', { rows: totalRows })}
                    </td>
                    <td className="px-4 py-2 text-sm" />
                    <td className="px-4 py-2 text-sm font-bold">
                      {formatCurrency(totalGross)}
                    </td>
                    <td colSpan={3} className="px-4 py-2 text-sm" />
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Status bar */}
            <div className="shrink-0 border-t px-4 py-1.5 text-xs text-muted-foreground">
              {loadedRows >= totalRows
                ? t('sales.rowsLoaded', { rows: totalRows })
                : t('sales.rowsLoadedOf', {
                    loaded: loadedRows,
                    total: totalRows,
                  })}
              {!flatQuery.hasNextPage &&
                loadedRows < totalRows &&
                t('sales.maxReached')}
            </div>
          </div>
        )}

        {!isLoading && isGrouped && groupedQuery.data && (
          <div className="space-y-2">
            <div className="text-sm text-muted-foreground">
              {t('sales.summary', {
                rows: totalRows,
                gross: formatCurrency(totalGross),
              })}
            </div>
            {groupedQuery.data.groups.map((group) => (
              <div key={group.key} className="rounded-md border">
                <button
                  className="flex w-full items-center justify-between p-3 text-left hover:bg-muted/50"
                  onClick={() => toggleGroup(group.key)}
                >
                  <div className="flex items-center gap-2">
                    {expandedGroup === group.key ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                    <span className="font-medium">
                      {formatGroupLabel(groupBy, group.key)}
                    </span>
                    <Badge variant="secondary">{group.rowCount}</Badge>
                  </div>
                  <span className="font-medium">
                    {formatCurrency(group.aggregates.totalGrossCents)}
                  </span>
                </button>

                {expandedGroup === group.key && (
                  <div className="border-t">
                    {expandQuery.isLoading ? (
                      <div className="p-4">
                        <Skeleton className="h-20 w-full" />
                      </div>
                    ) : expandQuery.data?.rows ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            {SALES_COLUMNS.map((col) => (
                              <TableHead key={col.key}>{col.label}</TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {expandQuery.data.rows.map((row) => (
                            <TableRow
                              key={row.id}
                              className={`cursor-pointer ${getRowClassName('status', row.orderStatus)}`}
                              onClick={() =>
                                setSelectedSale({
                                  id: row.id,
                                  channel: row.channel,
                                })
                              }
                            >
                              {SALES_COLUMNS.map((col) => (
                                <TableCell
                                  key={col.key}
                                  className={col.className}
                                >
                                  <CellRenderer column={col} row={row} />
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : null}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <SalesDetailDrawer
        saleId={selectedSale?.id ?? null}
        channel={selectedSale?.channel ?? null}
        open={selectedSale !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedSale(null)
        }}
      />
    </PermissionGate>
  )
}

function buildExpandFilters(
  groupBy: string,
  groupKey: string,
  baseFilters: Record<string, unknown>,
): Record<string, unknown> | null {
  if ((groupBy === 'operatorId' || groupBy === 'stationId') && groupKey === '–')
    return null

  const extra: Record<string, unknown> = {
    ...baseFilters,
    page: 1,
    pageSize: 50,
  }

  switch (groupBy) {
    case 'year':
      extra.dateFrom = `${groupKey}-01-01T00:00:00Z`
      extra.dateTo = `${groupKey}-12-31T23:59:59Z`
      break
    case 'quarter': {
      const [year, qStr] = groupKey.split('-Q')
      const qNum = Number(qStr)
      const startMonth = (qNum - 1) * 3 + 1
      const endMonth = startMonth + 2
      const lastDay = new Date(Number(year), endMonth, 0).getDate()
      extra.dateFrom = `${year}-${String(startMonth).padStart(2, '0')}-01T00:00:00Z`
      extra.dateTo = `${year}-${String(endMonth).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}T23:59:59Z`
      break
    }
    case 'month': {
      const [year, month] = groupKey.split('-')
      const lastDay = new Date(Number(year), Number(month), 0).getDate()
      extra.dateFrom = `${year}-${month}-01T00:00:00Z`
      extra.dateTo = `${year}-${month}-${String(lastDay).padStart(2, '0')}T23:59:59Z`
      break
    }
    case 'week': {
      const m = groupKey.match(/^(\d{4})-W(\d{2})$/)
      if (m) {
        const d = setISOWeek(
          setISOWeekYear(new Date(2000, 0, 1), Number(m[1])),
          Number(m[2]),
        )
        const weekStart = startOfISOWeek(d)
        const weekEnd = endOfISOWeek(d)
        extra.dateFrom = format(weekStart, "yyyy-MM-dd'T'00:00:00'Z'")
        extra.dateTo = format(weekEnd, "yyyy-MM-dd'T'23:59:59'Z'")
      }
      break
    }
    case 'day':
      extra.dateFrom = `${groupKey}T00:00:00Z`
      extra.dateTo = `${groupKey}T23:59:59Z`
      break
    case 'channel':
      extra.channel = groupKey
      break
    case 'restaurantId':
      extra.restaurantId = [groupKey]
      break
    case 'operatorId':
      extra.operatorId = [groupKey]
      break
    case 'stationId':
      extra.stationId = [groupKey]
      break
    case 'splitPayment':
      extra.splitPayment = [groupKey]
      break
    case 'orderStatus':
      extra.orderStatus = groupKey
      break
    case 'paymentStatus':
      extra.paymentStatus = groupKey
      break
    case 'paymentMethod':
      extra.paymentMethod = groupKey
      break
  }
  return extra
}

function CellRenderer({
  column,
  row,
}: {
  column: (typeof SALES_COLUMNS)[number]
  row: SalesRow
}) {
  const value = (row as unknown as Record<string, unknown>)[column.key]

  switch (column.type) {
    case 'datetime':
      return value ? (
        <span className="whitespace-nowrap">
          {formatDatetime(String(value))}
        </span>
      ) : null
    case 'currency':
      return (
        <span className="whitespace-nowrap font-medium">
          {formatCurrency(Number(value ?? 0))}
        </span>
      )
    case 'badge': {
      const entry = column.badgeMap?.[String(value)]
      if (!entry) return <span>{String(value ?? '')}</span>
      return <Badge variant={entry.variant ?? 'default'}>{entry.label}</Badge>
    }
    case 'boolean-badge': {
      if (column.hiddenWhenFalse && !value) return null
      const bEntry = column.badgeMap?.[String(value)]
      if (!bEntry) return null
      return <Badge variant={bEntry.variant ?? 'default'}>{bEntry.label}</Badge>
    }
    case 'text':
    default:
      return <span>{String(value ?? '')}</span>
  }
}
